import jjv from 'jjv'
import cache from '../core/cache'
import {sanitize} from '../core/extract'
import {anyRequired} from '../core/validate'
import {phoneFormat} from '../core/phonez'

export const USAGES = {
    residential: ['ResidentialParking', 'ResidentialCellar', 'ResidentialAnnex', 'Apartment', 'IndividualHouse'],
    tertiary: ['TertiaryParking', 'Workspace', 'Workstation', 'Office', 'RetailShop', 'Warehouse', 'GasStation'],
    service: ['MeetingRoom', 'Restaurant', 'Fitness', 'SPA', 'SportFacility', 'Pool', 'PoolHouse', 'Playground', 'Laundry', 'OtherService'],
    technical: ['BoilerRoom', 'Elevator', 'VentilationSystem', 'Gate', 'Intercom', 'BinStorageArea', 'MachineRoom', 'GardeningShed', 'OtherTechnical']
}

export const HOME_OWNER_ASSOCIATION_POSITIONS = ['Member', 'President']
export const NON_REFERENCED_LOT_POSITIONS = ['Tenant', 'Owner', 'Landlord']

export const CONTACT_PATRIMONY_LINK_TYPES = ['LinkToPatrimonyAsCareTaker', 'LinkToPatrimonyAsHomeOwnerAssociation', 'LinkToNonReferencedLot']
export const CONTACT_LOT_LINK_TYPES = ['LinkToLotAsTenant', 'LinkToLotAsOwner', 'LinkToLotAsLandlord']

export function createValidator() {
    const validator = jjv()

    validator.addCheck('anyRequired', anyRequired)
    validator.addFormat('phone', phoneFormat)
    return validator
}

export class Contact {
}

Contact.filterLinkedWith = (contact, {patrimony, lot}) => {
    const patrimonyUid = patrimony && patrimony.uid || patrimony
    const lotUid = lot && lot.uid || lot

    if (contact && contact.linkedWith) {
        if (patrimonyUid) {
            return contact.linkedWith.filter((l) => (l.patrimony && l.patrimony.uid) === patrimonyUid && l.linkType !== 'NonMember')
        }
        if (lotUid) {
            return CONTACT_LOT_LINK_TYPES.reduce((result, linkType) => {
                const link = contact.linkedWith.find((l) => l.linkType === linkType && (l.lot && l.lot.uid) === lotUid)
                if (link) {
                    result.push(link)
                }
                return result
            }, [])
        }
        return contact.linkedWith
    }
    return []
}

Contact.extractLinkedWith = (target, link) => {
    const linkType = sanitize(link && link.linkType)

    if (target) {
        const position = link && link.position
        const targetLinkType = target[linkType]

        switch(linkType) {
            case 'linkToPatrimonyAsHomeOwnerAssociation':
                return targetLinkType && targetLinkType[sanitize(position)] || linkType
            case 'linkToNonReferencedLot':
                return targetLinkType && targetLinkType[sanitize(link.linkedAs)] || linkType
            default:
                return targetLinkType || linkType
        }
    }
    return linkType
}

export class Criteria {
}

Criteria.schema = {
    type: 'object',
    properties: {
        fullText: {type: 'string'},
        agencyUids: {type: 'array'}
    }
}

export class ContactCriteria {
}

ContactCriteria.schema = {
    type: 'object',
    properties: {
        fullText: {type: 'string'},
        patrimonyUids: {type: 'string'}
    }
}

export class ReferencePatrimonyCmd {
}

ReferencePatrimonyCmd.schema = {
    type: 'object',
    properties: {
        ref: {type: 'string'},
        label: {type: 'string'},
        agencyUids: {
            type: 'array',
            minItems: 1
        }
    },
    required: ['ref', 'label', 'agencyUids']
}

export class ReferenceLotCmd {
}

ReferenceLotCmd.schema = {
    type: 'object',
    properties: {
        ref: {type: 'string'},
        lotNumber: {type: 'string'},
        usage: {type: 'string'},
        addresses: {
            type: 'array',
            minItems: 1,
            items: {
                type: 'object',
                properties: {
                    number: {type: ['string', 'null']},
                    street: {type: 'string'},
                    zipCode: {type: 'string'},
                    city: {type: 'string'},
                    recipientSupplement: {
                        properties: {
                            entrance: {type: ['string', 'null']},
                            elevator: {type: ['string', 'null']},
                            staircase: {type: ['string', 'null']},
                            floor: {type: ['string', 'null']},
                            unit: {type: ['string', 'null']}
                        }
                    },
                    digicode: {type: ['string', 'null']},
                    building: {
                        properties: {
                            uid: {type: ['string', 'null']},
                            label: {type: ['string', 'null']}
                        }
                    }
                },
                required: ['street', 'zipCode', 'city']
            }
        }
    },
    required: ['ref', 'lotNumber', 'usage', 'addresses']
}

export class ReferencePatrimonyContactCmd {
}

ReferencePatrimonyContactCmd.schemaCache = cache()

ReferencePatrimonyContactCmd.schema = {
    type: 'object',
    properties: {
        namez: {
            type: 'object',
            properties: {
                firstName: {type: 'string'},
                lastName: {type: 'string'},
                gender: {type: ['string', 'null']},
                company: {type: ['string', 'null']}
            },
            required: ['firstName', 'lastName']
        },
        linkedWithEntities: {
            oneOf: [{
                type: 'object',
                properties: {
                    lotUid: {type: 'string'},
                    linkType: {
                        type: 'string',
                        enum: ['LinkToLotAsTenant', 'LinkToLotAsOwner', 'LinkToLotAsLandlord']
                    }
                },
                required: ['lotUid', 'linkType']
            }, {
                type: 'object',
                properties: {
                    patrimonyUid: {type: 'string'},
                    linkType: {
                        type: 'string',
                        enum: ['LinkToPatrimonyAsCareTaker']
                    }
                },
                required: ['patrimonyUid', 'linkType']
            }, {
                type: 'object',
                properties: {
                    patrimonyUid: {type: 'string'},
                    position: {type: 'string'},
                    linkType: {
                        type: 'string',
                        enum: ['LinkToPatrimonyAsHomeOwnerAssociation']
                    }
                },
                required: ['patrimonyUid', 'position', 'linkType']
            }, {
                type: 'object',
                properties: {
                    patrimonyUid: {type: 'string'},
                    linkedAs: {type: 'string'},
                    linkType: {
                        type: 'string',
                        enum: ['LinkToNonReferencedLot']
                    }
                },
                required: ['patrimonyUid', 'linkedAs', 'linkType']
            }]
        },
        linkedWithPatrimonyAsHomeOwnerAssociation: {
            type: ['object', 'null'],
            properties: {
                patrimonyUid: {type: 'string'},
                position: {type: 'string'},
                linkType: {
                    type: 'string',
                    enum: ['LinkToPatrimonyAsHomeOwnerAssociation']
                }
            },
            required: ['patrimonyUid', 'position', 'linkType']
        },
        linkedToNonReferencedLot: {
            type: ['object', 'null'],
            properties: {
                patrimonyUid: {type: 'string'},
                position: {type: 'string'},
                linkType: {
                    type: 'string',
                    enum: ['LinkedToNonReferencedLot']
                }
            },
            required: ['patrimonyUid', 'position', 'linkType']
        },
        contacts: {
            type: 'array',
            minItems: 1,
            items: {
                oneOf: [{
                    type: 'object',
                    properties: {
                        phone: {
                            type: 'string',
                            format: 'phone'
                        }
                    },
                    required: ['phone']
                }, {
                    type: 'object',
                    properties: {
                        mail: {
                            type: 'string',
                            format: 'email'
                        }
                    },
                    required: ['mail']
                }, {
                    type: 'object',
                    properties: {
                        fax: {
                            type: 'string',
                            format: 'phone'
                        }
                    },
                    required: ['fax']
                }]
            }
        },
        address: {
            type: ['object', 'null'],
            properties: {
                number: {type: ['string', 'null']},
                street: {type: 'string'},
                zipCode: {type: 'string'},
                city: {type: 'string'},
                staircase: {type: ['string', 'null']},
                floor: {type: ['string', 'null']},
                digicode: {type: ['string', 'null']}
            },
            required: ['street', 'zipCode', 'city']
        }
    },
    required: ['namez', 'linkedWithEntities', 'contacts']
}

ReferencePatrimonyContactCmd.selectSchema = (cmd) => {
    const linkType = cmd && cmd.linkedWithEntities && cmd.linkedWithEntities.linkType || CONTACT_PATRIMONY_LINK_TYPES[0]

    return ReferencePatrimonyContactCmd.schemaCache(linkType, () => {
        const linkedWithEntitiesSchema = ReferencePatrimonyContactCmd.schema.properties.linkedWithEntities.oneOf.find((schema) => {
            return schema.properties.linkType.enum.indexOf(linkType) >= 0
        })

        const schema = {
            ...ReferencePatrimonyContactCmd.schema,
            properties: {
                ...ReferencePatrimonyContactCmd.schema.properties,
                linkedWithEntities: linkedWithEntitiesSchema
            }
        }

        if (linkType != 'LinkToLotAsOwner' && linkType != 'LinkToLotAsLandlord') {
            delete schema.properties.linkedWithPatrimonyAsHomeOwnerAssociation
        }
        return schema
    })
}

export class UpdatePatrimonyCmd {
}

UpdatePatrimonyCmd.schema = {
    type: 'object',
    properties: {
        ref: {type: 'string'},
        label: {type: 'string'}
    },
    required: ['ref', 'label']
}

export class UpdateLotCmd {
}

UpdateLotCmd.schema = {
    type: 'object',
    properties: {
        ref: {type: ['string', 'null']},
        lotNumber: {type: ['string', 'null']},
        usage: {type: ['string', 'null']}
    },
    anyRequired: ['ref', 'lotNumber', 'usage']
}

export class AddComplementaryAddressToPatrimonyCmd {
}

AddComplementaryAddressToPatrimonyCmd.schema = {
    type: 'object',
    properties: {
        complementaryAddress: {
            type: 'object',
            properties: {
                number: {type: ['string', 'null']},
                street: {type: 'string'},
                zipCode: {type: 'string'},
                city: {type: 'string'}
            },
            required: ['street', 'zipCode', 'city']
        },
    },
    required: ['complementaryAddress']
}

export class AddAddressToLotCmd {
}

AddAddressToLotCmd.schema = {
    type: 'object',
    properties: {
        address: {
            type: 'object',
            properties: {
                number: {type: ['string', 'null']},
                street: {type: 'string'},
                zipCode: {type: 'string'},
                city: {type: 'string'},
                recipientSupplement: {
                    properties: {
                        entrance: {type: ['string', 'null']},
                        elevator: {type: ['string', 'null']},
                        staircase: {type: ['string', 'null']},
                        floor: {type: ['string', 'null']},
                        unit: {type: ['string', 'null']}
                    }
                },
                digicode: {type: ['string', 'null']},
                building: {
                    properties: {
                        uid: {type: ['string', 'null']},
                        label: {type: ['string', 'null']}
                    }
                }
            },
            required: ['street', 'zipCode', 'city']
        },
    },
    required: ['address']
}

export class LinkPatrimonyContactWithCmd {
}

LinkPatrimonyContactWithCmd.schemaCache = cache()

LinkPatrimonyContactWithCmd.schema = {
    type: 'object',
    properties: {
        linkedWith: {
            oneOf: [{
                type: 'object',
                properties: {
                    lotUid: {type: 'string'},
                    linkType: {
                        type: 'string',
                        enum: ['LinkToLotAsTenant', 'LinkToLotAsOwner', 'LinkToLotAsLandlord']
                    }
                },
                required: ['lotUid', 'linkType']
            }, {
                type: 'object',
                properties: {
                    patrimonyUid: {type: 'string'},
                    linkType: {
                        type: 'string',
                        enum: ['LinkToPatrimonyAsCareTaker']
                    }
                },
                required: ['patrimonyUid', 'linkType']
            }, {
                type: 'object',
                properties: {
                    patrimonyUid: {type: 'string'},
                    position: {type: 'string'},
                    linkType: {
                        type: 'string',
                        enum: ['LinkToPatrimonyAsHomeOwnerAssociation']
                    }
                },
                required: ['patrimonyUid', 'position', 'linkType']
            }]
        }
    },
    required: ['linkedWith']
}

LinkPatrimonyContactWithCmd.selectSchema = (cmd) => {
    const linkType = cmd && cmd.linkedWith && cmd.linkedWith.linkType || CONTACT_PATRIMONY_LINK_TYPES[0]

    return LinkPatrimonyContactWithCmd.schemaCache(linkType, () => {
        const linkedWithSchema = LinkPatrimonyContactWithCmd.schema.properties.linkedWith.oneOf.find((schema) => {
            return schema.properties.linkType.enum.indexOf(linkType) >= 0
        })

        return {
            ...LinkPatrimonyContactWithCmd.schema,
            properties: {
                ...LinkPatrimonyContactWithCmd.schema.properties,
                linkedWith: linkedWithSchema
            }
        }
    })
}

export class UpdatePatrimonyContactIdentityCmd {
}

UpdatePatrimonyContactIdentityCmd.schema = {
    type: 'object',
    properties: {
        namez: {
            type: 'object',
            properties: {
                firstName: {type: 'string'},
                lastName: {type: 'string'},
                gender: {type: ['string', 'null']},
                company: {type: ['string', 'null']}
            },
            required: ['firstName', 'lastName']
        },
        address: {
            type: 'object',
            properties: {
                number: {type: ['string', 'null']},
                street: {type: 'string'},
                zipCode: {type: 'string'},
                city: {type: 'string'},
                staircase: {type: ['string', 'null']},
                floor: {type: ['string', 'null']},
                digicode: {type: ['string', 'null']}
            },
            required: ['street', 'zipCode', 'city']
        }
    },
    required: ['namez']
}

export class AddContactCmd {
}

AddContactCmd.schema = {
    type: 'object',
    properties: {
        contact: {
            oneOf: [{
                type: 'object',
                properties: {
                    phone: {
                        type: 'string',
                        format: 'phone'
                    }
                },
                required: ['phone']
            }, {
                type: 'object',
                properties: {
                    mail: {
                        type: 'string',
                        format: 'email'
                    }
                },
                required: ['mail']
            }, {
                type: 'object',
                properties: {
                    fax: {
                        type: 'string',
                        format: 'phone'
                    }
                },
                required: ['fax']
            }]
        },
    },
    required: ['contact']
}



// WEBPACK FOOTER //
// ./lib/patrimony/models.js